import React, { Component } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';

const Container = tw.div`flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-purple-600 to-indigo-800 p-8`;
const ErrorBox = tw.div`bg-white rounded-lg shadow-2xl p-8 max-w-lg w-full text-center`;
const ErrorIcon = tw.div`text-5xl mb-4`;
const ErrorTitle = tw.h1`text-3xl font-bold text-gray-800 mb-4`;
const ErrorMessage = tw.p`text-gray-600 mb-6 text-lg`;
const ErrorDetails = styled.pre`
  ${tw`text-left text-xs bg-gray-100 p-4 rounded overflow-auto text-gray-700 mb-6`}
  max-height: 10rem;
`;
const Button = styled.button`
  ${tw`px-8 py-3 bg-purple-600 text-white rounded-full font-bold hover:bg-purple-700 transition-colors duration-300 shadow-lg hover:shadow-xl`}
`;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // You can also log the error to an error reporting service like Sentry
    // if (process.env.NODE_ENV === 'production') {
    //   Sentry.captureException(error, { extra: errorInfo });
    // }

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleReload = () => {
    // Clear error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error and navigate to home
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container>
          <ErrorBox>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorTitle>Oops! Something went wrong</ErrorTitle>
            <ErrorMessage>
              We're sorry, but something unexpected happened. The error has been logged and we'll look into it.
            </ErrorMessage>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <ErrorDetails>
                <strong>Error:</strong> {this.state.error.toString()}
                {this.state.errorInfo && (
                  <>
                    <br /><br />
                    <strong>Component Stack:</strong>
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </ErrorDetails>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button onClick={this.handleReload}>
                🔄 Reload Page
              </Button>
              <Button onClick={this.handleGoHome}>
                🏠 Go Home
              </Button>
            </div>
          </ErrorBox>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
